import type { MaybeRefOrGetter } from '#imports';
import type { BeaconDisplayOptions, BeaconFunction, BeaconMethod } from '#shared/helpscout-beacon.config.dto';

// Module-level singleton — shared across all component instances (header, pending, results)
// Transient UI state: not serializable, does not belong in a store
const pendingOpen = ref(false);

// Below the persistent drawer (z-40) and the toasts, which would otherwise be covered by the fab.
const DISPLAY_CONFIG: BeaconDisplayOptions = { display: { zIndex: 30 } };

const handleReady = () => {
  console.log('[Beacon] handleReady fired, pendingOpen:', pendingOpen.value);
  if (pendingOpen.value) {
    pendingOpen.value = false;
    setTimeout(() => {
      globalThis.Beacon?.('navigate', '/ask/');
      globalThis.Beacon?.('open');
    }, 0);
  }
};

const openBeacon = () => {
  console.log('[Beacon] openBeacon, info:', globalThis.Beacon?.('info'), 'pendingOpen:', pendingOpen.value);
  if (globalThis.Beacon?.('info')) {
    globalThis.Beacon('navigate', '/ask/');
    globalThis.Beacon('open');
    return;
  }
  pendingOpen.value = true;
};

// Style the fab had before it was taken off, set in the Beacon Builder — restored as is.
let styleBeforeHide: string | undefined;

// One entry per caller currently asking the fab off: it comes back only once none is left,
// so the search edit panel closing does not bring it back over a toast still on screen.
const fabHiders = new Set<symbol>();

// HelpScout has no show/hide method for the fab: 'manual' style unmounts it, and config merges
// (zIndex stays). hideFABOnMobile does nothing once the Beacon is loaded (checked in the browser).
const applyFabHidden = () => {
  const hidden = fabHiders.size > 0;
  if (hidden && styleBeforeHide === undefined) {
    styleBeforeHide = globalThis.Beacon?.('info')?.config?.display?.style;
    if (styleBeforeHide !== undefined) {
      globalThis.Beacon?.('config', { display: { style: 'manual' } });
    }
    return;
  }
  if (!hidden && styleBeforeHide !== undefined) {
    globalThis.Beacon?.('config', { display: { style: styleBeforeHide } });
    styleBeforeHide = undefined;
  }
};

// Keeps the fab off while `hidden` is true, and lets go of it when the calling scope is disposed.
// Needs none of the setup below, so callers skip useHelpscoutBeacon and its script registration.
export const useBeaconFabHidden = (hidden: MaybeRefOrGetter<boolean>) => {
  const hider = Symbol('beacon-fab-hider');
  const update = (isHidden: boolean) => {
    if (isHidden) {
      fabHiders.add(hider);
    } else {
      fabHiders.delete(hider);
    }
    applyFabHidden();
  };

  watch(() => toValue(hidden), update, { immediate: true });
  onScopeDispose(() => {
    update(false);
  });
};

export const useHelpscoutBeacon = () => {
  const { $appConfig } = useNuxtApp();
  const config = $appConfig.app?.composables?.helpscoutBeacon;
  const scriptSource = $appConfig.beacon?.scriptSrc;

  const beaconId = useLocalized(config?.ids);

  // trigger: 'manual' — no preload hint injected; load() called explicitly in initBeacon after mount
  // crossorigin: false — HelpScout CDN has no CORS headers; crossorigin="anonymous" would block the request
  const { load } = useScript({ src: scriptSource ?? '#', crossorigin: false }, { trigger: 'manual' });

  const initBeacon = async () => {
    if (!scriptSource || !beaconId.value || globalThis.Beacon?.('info')) return;

    // Script 1 — stub (mirrors official embed snippet): window.Beacon queues calls until real API loads
    // Typed by hand: overloaded call signatures give no contextual one. `undefined` also answers 'info' — not mounted yet.
    const stub: BeaconFunction = (method: BeaconMethod, options?: unknown, data?: unknown): undefined => {
      stub.readyQueue ??= [];
      stub.readyQueue.push({ method, options, data });
    };
    globalThis.Beacon = stub;

    // Script 2 — init (mirrors official embed second <script> tag)
    globalThis.Beacon('init', beaconId.value);

    globalThis.Beacon('config', DISPLAY_CONFIG);

    // once ready — queued before load() so HelpScout processes it on startup alongside init
    globalThis.Beacon('once', 'ready', handleReady);

    await load(); // entry script loads vendor + main; main.js reads readyQueue on startup
  };

  // Locale change — script already loaded, 'ready' does not re-fire after destroy+init
  const switchBeacon = () => {
    if (!beaconId.value || !globalThis.Beacon?.('info')) return;
    globalThis.Beacon('destroy');
    globalThis.Beacon('init', beaconId.value);
    globalThis.Beacon('config', DISPLAY_CONFIG);
  };

  return { initBeacon, switchBeacon, openBeacon };
};
